import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { naira, healthTone, daysLabel } from "@/lib/helpers";
import { HealthRing, StatCard } from "@/components/widgets";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Wrench, FileText, Fuel, Route, CloudRain, ShieldAlert, ScanLine, ChevronRight, Crown, MessageCircle,
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-40 w-full rounded-3xl" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}
        </div>
      </div>
    );
  }

  const t = healthTone(data.health_score);
  const v = data.vehicle;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-start justify-between vp-fade-up">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950">
            {data.greeting} 👋
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your VecPadi is your Smart Digital Vehicle Companion.
          </p>
        </div>
        {data.user.plan !== "premium" && (
          <Button data-testid="dash-upgrade" onClick={() => navigate("/app/premium")} size="sm"
            className="rounded-full bg-zinc-900 hover:bg-zinc-800 gap-1.5 hidden sm:flex">
            <Crown size={15} /> Premium
          </Button>
        )}
      </div>

      {/* Health hero */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm vp-fade-up">
        {v ? (
          <div className="flex items-center gap-6">
            <HealthRing score={data.health_score} />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-400">Vehicle Health</div>
              <div className={`font-display text-xl font-bold ${t.tone}`}>{t.label}</div>
              <p className="mt-1 text-sm text-zinc-600">{t.msg}</p>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600">
                {v.make} {v.model} • {v.year}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">No car added yet.</p>
            <Button onClick={() => navigate("/onboarding")} className="mt-3 rounded-full bg-emerald-500 hover:bg-emerald-600">Add your car</Button>
          </div>
        )}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="stat-service" label="Next Service" icon={Wrench} tone="amber" onClick={() => navigate("/app/care")}
          value={data.next_service ? `${data.next_service.days}d` : "—"}
          sub={data.next_service ? data.next_service.title : "Nothing due"} />
        <StatCard testid="stat-docs" label="Documents" icon={FileText}
          tone={data.doc_alerts.length ? "red" : "emerald"} onClick={() => navigate("/app/care")}
          value={data.doc_alerts.length ? data.doc_alerts.length : "OK"}
          sub={data.doc_alerts.length ? `${data.doc_alerts[0].doc_type} soon` : "All valid"} />
        <StatCard testid="stat-fuel" label="Fuel (Month)" icon={Fuel} onClick={() => navigate("/app/expenses")}
          value={naira(data.fuel_month)} sub="This month" />
        <StatCard testid="stat-total" label="Total Spend" icon={Fuel} onClick={() => navigate("/app/expenses")}
          value={naira(data.total_month)} sub="This month" />
      </div>

      {/* Document alerts */}
      {data.doc_alerts.length > 0 && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="doc-alert-banner">
          <div className="flex items-center gap-2 text-amber-700">
            <FileText size={18} />
            <span className="font-semibold text-sm">Renewal reminders</span>
          </div>
          <div className="mt-3 space-y-2">
            {data.doc_alerts.slice(0, 3).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-zinc-700">{d.doc_type}</span>
                <span className={`font-semibold ${d.days < 7 ? "text-red-600" : "text-amber-700"}`}>
                  {d.days < 0 ? "Expired" : `${d.days} days`}
                </span>
              </div>
            ))}
          </div>
          <Button data-testid="doc-renew" onClick={() => navigate("/app/care")} size="sm" variant="outline" className="mt-4 rounded-full border-amber-300 bg-white text-amber-700 hover:bg-amber-100">Renew Now</Button>
        </div>
      )}

      {/* Traffic & Weather */}
      <div className="grid gap-4 sm:grid-cols-2">
        <button onClick={() => navigate("/app/drive")} data-testid="dash-traffic"
          className="text-left rounded-3xl border border-zinc-200 bg-white p-5 transition-transform hover:-translate-y-1 hover:shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-zinc-700"><Route size={18} className="text-emerald-600" /><span className="font-semibold text-sm">Traffic</span></div>
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">{data.traffic.status}</span>
          </div>
          <p className="mt-3 text-sm text-zinc-600">{data.traffic.message}</p>
          <p className="mt-1 text-xs text-zinc-400">{data.traffic.route} • {data.traffic.eta_min} min</p>
        </button>
        <button onClick={() => navigate("/app/drive")} data-testid="dash-weather"
          className="text-left rounded-3xl border border-zinc-200 bg-white p-5 transition-transform hover:-translate-y-1 hover:shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-zinc-700"><CloudRain size={18} className="text-emerald-600" /><span className="font-semibold text-sm">Climate</span></div>
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">{data.weather.temp_c}°C</span>
          </div>
          <p className="mt-3 text-sm text-zinc-600">{data.weather.message}</p>
          <p className="mt-1 text-xs text-zinc-400">Rain {data.weather.rain_chance}% • Flood: {data.weather.flood_risk}</p>
        </button>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Quick actions</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { icon: ScanLine, label: "AI Scan", to: "/app/scan", color: "text-emerald-600 bg-emerald-50" },
            { icon: MessageCircle, label: "Ask VecPadi", to: "/app/assistant", color: "text-blue-600 bg-blue-50" },
            { icon: ShieldAlert, label: "Emergency", to: "/app/safety", color: "text-red-600 bg-red-50" },
            { icon: Wrench, label: "Car Care", to: "/app/care", color: "text-amber-600 bg-amber-50" },
          ].map((a) => (
            <button key={a.label} data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => navigate(a.to)}
              className="flex flex-col items-center gap-2 rounded-3xl border border-zinc-200 bg-white p-5 transition-transform hover:-translate-y-1 hover:shadow-md active:scale-95">
              <div className={`grid h-12 w-12 place-items-center rounded-2xl ${a.color}`}><a.icon size={22} /></div>
              <span className="text-xs font-medium text-zinc-700">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Safety strip */}
      <button onClick={() => navigate("/app/safety")} data-testid="dash-sos"
        className="flex w-full items-center justify-between rounded-3xl bg-red-500 p-5 text-white transition-transform hover:-translate-y-0.5 active:scale-[0.99]">
        <div className="flex items-center gap-3">
          <ShieldAlert size={24} />
          <div className="text-left">
            <div className="font-display font-bold">Emergency SOS</div>
            <div className="text-xs text-red-100">One tap for help on the road</div>
          </div>
        </div>
        <ChevronRight />
      </button>
    </div>
  );
}
